<template>
  <footer>
    <div v-if="isCancelled">
      <p class="response-message">
        <LsIcon type="cancel" color="danger" size="md" class="response-icon" />
        <strong test-id="long_grass_quote_footer-cancelled_on">{{
          t('canceledOnDate', {
            date: formattedDateOn,
          })
        }}</strong>
      </p>
    </div>
    <div v-if="!isCancelled && isExistingHash">
      <p class="response-message">
        <LsIcon type="exclamation" color="tertiary" size="md" class="response-icon" />
        <strong test-id="long_grass_quote_footer-support_will_contact">{{
          t('supportAgentWillReachYou', { brandName })
        }}</strong>
      </p>
    </div>
    <div v-if="!showApproved && !showDateSelection && isExpired">
      <p class="response-message">
        <LsIcon type="denied" color="danger" size="md" class="response-icon" />
        <strong test-id="long_grass_quote_footer-expired">
          {{ t('longGrass.longGrassReport.expired.label') }}
        </strong>
      </p>
      <p class="response-message">{{ t('longGrass.longGrassReport.expired.subLabel') }}</p>
    </div>

    <div v-if="showApproved">
      <p class="response-message">
        <LsIcon type="checkfull" color="primary" size="md" class="response-icon" />
        <strong test-id="long_grass_quote_footer-accepted_on">{{
          t('longGrass.longGrassReport.acceptedLongGrassQuoteOnDate', {
            date: formattedDateOn,
          })
        }}</strong>
      </p>
    </div>
    <div v-if="showDateSelection">
      <LsRadios
        v-model="selectedReturnDate"
        name="selectedReturnDate"
        class="select-return-date"
        :options="returnDatesAvailable"
        :disabled="returnDatesAvailable.length === 0"
        :validation="{ required: true }"
        :label="t('whenProCanReturn')"
        track-id="long_grass_quote_footer-select_return_date"
        test-id="long_grass_quote_footer-select_return_date"
      />

      <LsButton
        :text="t('rejectService')"
        bg-color="primary-light"
        track-id="long_grass_quote_footer-reject_service"
        test-id="long_grass_quote_footer-reject_service"
        class="reject-btn"
        @click="rejectLongGrassQuote()"
      />

      <LsButton
        :text="t('scheduleService')"
        bg-color="primary"
        track-id="long_grass_quote_footer-schedule_service"
        test-id="long_grass_quote_footer-schedule_service"
        @click="acceptLongGrassQuote()"
      />
    </div>
  </footer>
</template>

<script>
import { themedComponentMixin } from '@/mixins';
import VueTypes from 'vue-types';
import LsRadios from '@/components/common/LsRadios/LsRadios.vue';
import LsButton from '@/components/common/LsButton/LsButton.vue';
import LsIcon from '@/components/common/LsIcon/LsIcon.vue';
import { mapActions } from 'vuex';
import { lsStorageService } from 'ls-vue-common';
import { SERVICE_HISTORY__GET_SCHEDULE } from '@/store/modules/service-history/types';
import { INSTANT_QUOTE__ACCEPT_PENDING_QUOTE } from '@/store/modules/instant-quote/types';
import moment from 'moment';

export default {
  name: 'LgQuoteFooter',
  $_veeValidate: {
    validator: 'new',
  },
  components: { LsIcon, LsRadios, LsButton },
  mixins: [themedComponentMixin],
  props: {
    isPending: {
      type: VueTypes.bool.required,
      required: true,
    },
    isCancelled: {
      type: VueTypes.bool.required,
      required: true,
    },
    displayedDateOn: {
      type: VueTypes.string.required,
      required: false,
    },
    returnDatesAvailable: VueTypes.arrayOf(
      VueTypes.shape({
        label: VueTypes.string.isRequired,
        value: VueTypes.string.isRequired,
      }).isRequired.loose
    ).isRequired,
  },
  data() {
    return {
      selectedReturnDate: '',
    };
  },
  computed: {
    propertyId() {
      return this.$route.params.property_id;
    },
    scheduleId() {
      return this.$route.params.schedule_id;
    },
    scheduleEventId() {
      return this.$route.params.scheduleevent_id;
    },
    reportId() {
      return this.$route.params.report_id;
    },
    quoteHash() {
      return [this.propertyId, this.scheduleId, this.scheduleEventId, this.reportId].join('');
    },
    isExistingHash() {
      return lsStorageService.get(this.quoteHash) || false;
    },
    formattedDateOn() {
      const momentDate = moment.utc(this.displayedDateOn).local();
      return momentDate.format('ddd, MMM D, YYYY, h:mma');
    },
    isExpired() {
      return this.returnDatesAvailable.length === 0;
    },
    showDateSelection() {
      const { isCancelled, isPending, isExistingHash, isExpired } = this;
      return !isExpired && !isCancelled && !isExistingHash && isPending;
    },
    showApproved() {
      const { isCancelled, isPending, isExistingHash } = this;
      return !isCancelled && !isPending && !isExistingHash;
    },
  },
  methods: {
    ...mapActions({
      SERVICE_HISTORY__GET_SCHEDULE,
      INSTANT_QUOTE__ACCEPT_PENDING_QUOTE,
    }),
    async acceptLongGrassQuote() {
      if (await this.$validator.validateAll()) {
        await this[INSTANT_QUOTE__ACCEPT_PENDING_QUOTE]({
          property_id: this.propertyId,
          instantQuoteId: this.reportId,
          selected_date: this.selectedReturnDate,
          onSuccess: this.onSuccessCallback,
        });
      }
    },
    async onSuccessCallback() {
      await this[SERVICE_HISTORY__GET_SCHEDULE]({
        property_id: this.propertyId,
        schedule_id: this.scheduleId,
      });

      this.$router.push({
        name: 'LongGrassScheduledServiceView',
      });
    },
    rejectLongGrassQuote() {
      this.$router.push({
        name: 'LongGrassRejectedServiceView',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.reject-btn {
  margin-right: 1.5rem;

  @media (max-width: $mobile-max-width) {
    margin-bottom: 20px;
  }
}
.select-return-date {
  padding: 1.5rem 0 2.5rem;
  border-top: 1px solid $theme-color-primary-light;
}
.response-message {
  display: flex;
  font-size: 1.4rem;
  margin-bottom: 1rem;

  .response-icon {
    flex-shrink: 0;
    margin-right: 1rem;
    width: 2.3rem;
  }
}
</style>
